import React from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
} from "@mui/material";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Link as RouterLink } from "react-router-dom";
import AuthService from "../services/authService";

const CONTACT_EMAIL = "contact@voras.com";

// Cartel del soft-cap (402 egress_limit_reached): el cupo del plan está lleno. Política:
// las VENTAS de los canales se siguen recibiendo (nunca se pierden); lo bloqueado es
// PROCESARLAS (preparar/despachar) y crear egresos manuales, hasta mejorar el plan.
// La acción de mejorar hoy es contacto/planes (mailto); con Stripe será Checkout/Portal.
const EgressLimitModal = ({ open, onClose, plan, limit, used }) => {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 100;

  const mailto =
    `mailto:${CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent("Mejorar plan Voras")}` +
    `&body=${encodeURIComponent(
      `Hola, alcancé el límite de egresos de mi plan${plan ? ` ${plan}` : ""} y quiero mejorarlo.`
    )}`;

  // /plans es owner-only: el owner va a la sección de planes; el colaborador mantiene
  // el contacto (no puede gestionar el plan).
  const ctaProps = AuthService.isOwner()
    ? { component: RouterLink, to: "/plans" }
    : { href: mailto };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      {/* Header con gradiente cálido + ícono */}
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(135deg, #F2651A 0%, #d97706 100%)",
          px: 3,
          pt: 3.5,
          pb: 3,
          textAlign: "center",
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
          aria-label="Cerrar"
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: "auto",
            mb: 1.5,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RocketLaunchRoundedIcon sx={{ fontSize: 34, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", lineHeight: 1.25 }}>
          ¡Tu operación creció!
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: "0.9rem", opacity: 0.95 }}>
          Alcanzaste el límite de egresos de tu plan
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
        {/* Uso del cupo */}
        <Box
          sx={{
            backgroundColor: "#FFF7ED",
            border: "1px solid #FED7AA",
            borderRadius: "12px",
            p: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.75,
            }}
          >
            <Chip
              label={`Plan ${plan || "actual"}`}
              size="small"
              sx={{ backgroundColor: "#FFEDD5", color: "#9A3412", fontWeight: 700, height: 22 }}
            />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#9A3412" }}>
              {used} / {limit} egresos
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: "#FFEDD5",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                background: "linear-gradient(90deg, #F2651A, #dc2626)",
              },
            }}
          />
          <Typography sx={{ mt: 0.5, fontSize: "0.72rem", color: "#9A3412", textAlign: "right" }}>
            {pct}% del cupo mensual usado
          </Typography>
        </Box>

        {/* Tranquilidad: las ventas no se pierden */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#16a34a", mt: "2px" }} />
          <Typography sx={{ fontSize: "0.88rem", color: "#374151" }}>
            <strong>Tus ventas se siguen recibiendo</strong> de todos los canales y quedan
            pendientes — no se pierde ninguna.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2.5 }}>
          <RocketLaunchRoundedIcon sx={{ fontSize: 18, color: "#F2651A", mt: "2px" }} />
          <Typography sx={{ fontSize: "0.88rem", color: "#374151" }}>
            Para <strong>prepararlas y despacharlas</strong>, mejorá tu plan y seguí operando al
            instante.
          </Typography>
        </Box>

        {/* CTAs */}
        <Button
          {...ctaProps}
          onClick={onClose}
          fullWidth
          variant="contained"
          disableElevation
          startIcon={<RocketLaunchRoundedIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            py: 1.1,
            fontSize: "0.95rem",
            backgroundColor: "#F2651A",
            "&:hover": { backgroundColor: "#d95513" },
          }}
        >
          Mejorar mi plan
        </Button>
        <Button
          onClick={onClose}
          fullWidth
          sx={{ mt: 1, textTransform: "none", color: "#6b7280", fontSize: "0.85rem" }}
        >
          Ahora no
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default EgressLimitModal;
